import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import usePbDocumentsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

// mui-datatables@4.3.0 `options.filterList`'i okumaz (bkz.
// reference_datatable_filterlist_ignored) — açılıştaki `status[in]=uploaded` varsayılanı
// bir DataTable çipiyle GÖSTERİLEMEZ. EmployeeDocumentsPage'teki aynı şerit deseni.
const FilterBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: rgba(2, 145, 239, 0.07);
  border: 1.5px solid ${({ theme }) => theme.colors.primary.main};
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  padding: 10px 14px;
  margin-bottom: 12px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FilterBannerClear = styled.button`
  font-family: inherit;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 0;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.main};
  padding: 4px 2px;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
`;

// Backend'in status filtresi `status[in]` operatörünü kullanıyor (bkz. `status` sütunu,
// aynı isim). Varsayılan aktifken kullanıcı status sütununu KENDİSİ filtrelerse
// (tableParams zaten bu anahtarı taşırsa) zorlama devre dışı kalır — aksi halde kullanıcının
// seçtiği durum, sessizce "uploaded" ile ezilirdi.
const DEFAULT_STATUS_KEY = 'status[in]';
const DEFAULT_STATUS_VALUE = 'uploaded';

export default function PbDocumentsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('PB') : false;

  // Bu ekran artık arşiv değil BEKLEYEN İŞLER görünümü: açılışta yalnız onay bekleyen
  // (`uploaded`) belgeler görünür. `defaultStatusCleared` DataTable'ın sayfalama/sıralama
  // state'inden AYRI tutulur — aksi halde her `onTableChanged` çağrısı `parseTableParams`'ın
  // ürettiği TAZE params objesiyle varsayılan filtreyi sessizce düşürür.
  const [tableParams, setTableParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [defaultStatusCleared, setDefaultStatusCleared] = useState(false);
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [viewing, setViewing] = useState(null); // { fileUrl, title }

  // drawer: { mode: 'create' } | { mode: 'edit', initialValues }
  const [drawer, setDrawer] = useState(null);

  const hasExplicitStatusFilter = Object.prototype.hasOwnProperty.call(tableParams, DEFAULT_STATUS_KEY);
  const showingDefaultStatus = !defaultStatusCleared && !hasExplicitStatusFilter;
  const queryParams = useMemo(
    () => (showingDefaultStatus ? { ...tableParams, [DEFAULT_STATUS_KEY]: DEFAULT_STATUS_VALUE } : tableParams),
    [tableParams, showingDefaultStatus],
  );

  const {
    fetching, data, pagination, columns: baseColumns,
    objectSchema, toFormValues, create, update, approve, reject, waive, remove,
  } = usePbDocumentsApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setTableParams(params);
  }, [baseColumns]);

  const handleClearDefaultStatus = useCallback(() => setDefaultStatusCleared(true), []);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleNewButtonClick = useCallback(() => setDrawer({ mode: 'create' }), []);

  const handleSave = useCallback(async (formData) => {
    const success = drawer?.mode === 'edit'
      ? await update(drawer.initialValues.id, formData)
      : await create(formData);
    if (success) setDrawer(null);
    return success;
  }, [drawer, create, update]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const actions = [];

    // Müşteri şikayeti: "dosya açılamıyor". Ham fileUrl ASLA açılmaz — erişim kararını
    // FileAccessGuard verir, imzalı adres DocumentViewerModal üzerinden (kendi
    // /v1/storage/view çağrısıyla) alınır; belge artık yeni sekmede değil uygulama-içi
    // modalda açılır (ürün sahibi kararı). Bu görüntüleme aksiyonu KASITLI olarak
    // `writable` denetiminden ÖNCE gelir: dosyayı açmak bir OKUMA işlemidir,
    // `/preboardings/documents` rotası PB.Read-only kullanıcılar için de açık
    // (routes/index.js: `privileges={['PB.Read', 'PB.Write']}`) ve gerçek erişim kararını
    // zaten sunucu (FileAccessGuard) veriyor — buton onu tekrar kısıtlarsa PB.Read'e sahip
    // ama PB.Write'ı olmayan bir kullanıcı için "dosya açılamıyor" şikayeti bu ekranda AYNEN
    // sürer.
    if (row.fileUrl) {
      actions.push({
        type: 'edit',
        label: t('personnelFile.openFile'),
        onClick: () => setViewing({ fileUrl: row.fileUrl, title: row.title }),
      });
    }

    if (!writable) return actions;

    if (row.status === 'pending' || row.status === 'rejected') {
      actions.push({
        type: 'edit',
        label: t('common.edit'),
        onClick: () => setDrawer({ mode: 'edit', initialValues: toFormValues(row) }),
      });
    }
    if (row.status === 'uploaded') {
      actions.push({
        type: 'edit',
        label: t('preboardingDoc.action.approve'),
        onClick: () => setConfirm({
          message: t('preboardingDoc.confirm.approve', { title: row.title }),
          onYes: () => approve(row.id),
        }),
      });
      actions.push({
        type: 'delete',
        label: t('preboardingDoc.action.reject'),
        onClick: () => setConfirm({
          message: t('preboardingDoc.confirm.reject', { title: row.title }),
          onYes: () => reject(row.id, t('preboardingDoc.defaultRejectReason')),
        }),
      });
    }
    if (row.status === 'pending' || row.status === 'rejected') {
      actions.push({
        type: 'edit',
        label: t('preboardingDoc.action.waive'),
        onClick: () => setConfirm({
          message: t('preboardingDoc.confirm.waive', { title: row.title }),
          onYes: () => waive(row.id, t('preboardingDoc.defaultWaiveReason')),
        }),
      });
    }
    if (row.status !== 'approved' && row.status !== 'waived' && row.status !== 'cancelled') {
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: () => setConfirm({
          message: t('preboardingDoc.confirm.delete', { title: row.title }),
          onYes: () => remove(row.id),
        }),
      });
    }
    return actions;
  }, [t, writable, approve, reject, waive, remove, toFormValues]);

  // Yazma yetkisi olmayan (PB.Read-only) kullanıcı için de aksiyon kolonu eklenir, EĞER
  // sayfadaki en az bir satırda görüntülenebilir bir dosya varsa — aksi halde kolon boş
  // kalır ve gereksiz yer kaplar. `writable` her zaman ekler (onay/reddet/feragat/sil
  // aksiyonları satır durumuna göre zaten filtrelenir, bkz. buildActions).
  const showActionsColumn = writable || data.some((row) => row?.fileUrl);

  const columns = useMemo(() => {
    if (!showActionsColumn) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, showActionsColumn]);

  return (
    <Container>
      <GenericFormDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.mode === 'edit' ? t('preboardingDoc.edit') : t('preboardingDoc.new')}
        schema={objectSchema}
        initialValues={drawer?.mode === 'edit' ? drawer.initialValues : null}
        onSave={handleSave}
      />

      {showingDefaultStatus && (
        <FilterBanner>
          <span>{t('preboardingDoc.filterBanner')}</span>
          <FilterBannerClear type="button" onClick={handleClearDefaultStatus}>
            {t('preboardingDoc.filterBannerClear')}
          </FilterBannerClear>
        </FilterBanner>
      )}

      {useMemo(() => (
        <DataTable
          title={t('preboardingDoc.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [t, fetching, columns, data, pagination, writable, handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </Container>
  );
}
